import type {
  GetMbcCatalogResponse,
  ListMbcAdminAssignmentsResponse,
  MbcAdminCheck,
  MbcAdminCriterion,
  MbcAdminGuideline,
} from "@nlc/shared-types";

import { mbcAdminGuidelineBodyZod } from "@nlc/shared-types";
import { useNavigate } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/button";
import { ConfirmationModal } from "@/components/confirmation-modal";
import {
  Description,
  Input,
  Label,
  TextArea,
  TextField,
} from "@/components/field";
import { Switch, SwitchField } from "@/components/switch";
import { toast } from "@/components/toast/toast-queue";
import { FeaturePageLayout } from "@/features/shared/layout/FeaturePageLayout";

import { useChecks } from "../checks/hooks/useChecks";
import { useAssignDefaultGuideline } from "../design-types/hooks/useAssignDefaultGuideline";
import { useAssignments } from "../design-types/hooks/useAssignments";
import { useMbcCatalog } from "../design-types/hooks/useMbcCatalog";
import { REQUIRED_TOTAL_WEIGHT, toBody, totalWeight } from "./guideline-body";
import { GuidelineChecksSection } from "./GuidelineChecksSection";
import { GuidelineCriteriaSection } from "./GuidelineCriteriaSection";
import { GuidelineUsedBy } from "./GuidelineUsedBy";
import { useDeleteGuideline } from "./hooks/useDeleteGuideline";
import { useGuidelines } from "./hooks/useGuidelines";
import { useSaveGuideline } from "./hooks/useSaveGuideline";

const LIST_HREF = "/admin/mbc?tab=guidelines";

interface GuidelinePageProps {
  readonly guidelineId: null | string;
}

export function GuidelinePage({ guidelineId }: GuidelinePageProps) {
  const guidelines = useGuidelines();
  const checks = useChecks();
  const assignments = useAssignments();
  const catalog = useMbcCatalog();

  const isLoading = [guidelines, checks, assignments, catalog].some(
    (query) => query.isLoading,
  );
  const guideline = guidelineId
    ? guidelines.data?.find((entry) => entry._id === guidelineId)
    : null;
  const title = guidelineId ? "Edit guideline" : "New guideline";

  if (isLoading)
    return (
      <FeaturePageLayout isLoading title={title}>
        {null}
      </FeaturePageLayout>
    );

  if (
    guideline === undefined ||
    !guidelines.data ||
    !checks.data ||
    !assignments.data ||
    !catalog.data
  ) {
    return (
      <div className="p-xl">
        <FeaturePageLayout backButton title={title}>
          <p
            className="rounded-b-xl bg-bg-primary p-6 text-sm text-text-tertiary"
            role="alert"
          >
            {guidelines.error
              ? `Could not load the guideline: ${guidelines.error.message}`
              : "Guideline not found"}
          </p>
        </FeaturePageLayout>
      </div>
    );
  }

  return (
    <GuidelineForm
      assignments={assignments.data}
      catalog={catalog.data}
      checks={checks.data}
      guideline={guideline}
      title={title}
    />
  );
}

interface Problems {
  details: string[];
  save: string[];
}

const NO_PROBLEMS: Problems = { details: [], save: [] };

interface GuidelineFormProps {
  readonly assignments: ListMbcAdminAssignmentsResponse;
  readonly catalog: GetMbcCatalogResponse;
  readonly checks: MbcAdminCheck[];
  readonly guideline: MbcAdminGuideline | null;
  readonly title: string;
}

function GuidelineForm({
  assignments,
  catalog,
  checks,
  guideline,
  title,
}: GuidelineFormProps) {
  const navigate = useNavigate();
  const [name, setName] = useState(guideline?.name ?? "");
  const [instructions, setInstructions] = useState(guideline?.guideline ?? "");
  const [checkKeys, setCheckKeys] = useState(guideline?.checkKeys ?? []);
  const [criteria, setCriteria] = useState<MbcAdminCriterion[]>(
    guideline?.criteria ?? [],
  );
  const isCurrentDefault =
    guideline !== null && assignments.default.guidelineId === guideline._id;
  const [isDefault, setIsDefault] = useState(isCurrentDefault);
  const [defaultNotes, setDefaultNotes] = useState(
    assignments.default.notes ?? "",
  );
  const [problems, setProblems] = useState(NO_PROBLEMS);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const saveGuideline = useSaveGuideline();
  const deleteGuideline = useDeleteGuideline();
  const assignDefaultGuideline = useAssignDefaultGuideline();

  const goToList = () => navigate({ href: LIST_HREF });

  const assignDefault = (guidelineId: string) => {
    const notes = defaultNotes.trim() || null;

    if (isCurrentDefault && notes === assignments.default.notes) {
      goToList();

      return;
    }

    assignDefaultGuideline.mutate(
      { guidelineId, notes },
      {
        onError: (error) =>
          toast.add({
            description: error.message,
            title: "Guideline saved, but it could not be made the default",
            type: "error",
          }),
        onSettled: goToList,
      },
    );
  };

  const handleSave = () => {
    const parsed = mbcAdminGuidelineBodyZod.safeParse(
      toBody({ checkKeys, checks, criteria, guideline: instructions, name }),
    );

    if (!parsed.success) {
      setProblems({
        ...NO_PROBLEMS,
        details: parsed.error.issues.map(
          (issue) => `${issue.path.join(".")}: ${issue.message}`,
        ),
      });

      return;
    }

    const total = totalWeight(criteria);

    if (total !== REQUIRED_TOTAL_WEIGHT) {
      setProblems({
        ...NO_PROBLEMS,
        save: [
          `The criteria weights must add up to ${REQUIRED_TOTAL_WEIGHT}%, they add up to ${total}%`,
        ],
      });

      return;
    }

    setProblems(NO_PROBLEMS);
    saveGuideline.mutate(
      { fields: parsed.data, guidelineId: guideline?._id ?? null },
      {
        onError: (error) =>
          setProblems({ ...NO_PROBLEMS, save: error.message.split("\n") }),
        onSuccess: (savedGuideline) =>
          isDefault ? assignDefault(savedGuideline._id) : goToList(),
      },
    );
  };

  const handleDelete = () => {
    if (!guideline) return;

    deleteGuideline.mutate(guideline._id, {
      onError: (error) => {
        setIsDeleteModalOpen(false);
        toast.add({
          description: error.message,
          title: "Could not delete the guideline",
          type: "error",
        });
      },
      onSuccess: goToList,
    });
  };

  return (
    <div className="p-xl">
      <FeaturePageLayout
        backButton
        headerContent={
          guideline && (
            <Button
              className="ml-auto"
              color="destructive"
              onPress={() => setIsDeleteModalOpen(true)}
              size="lg"
              variant="tertiary"
            >
              Delete
            </Button>
          )
        }
        title={title}
      >
        <div className="flex flex-col divide-y divide-border-secondary rounded-b-xl bg-bg-primary">
          <section className="p-6">
            <h2 className="text-md font-semibold text-text-primary">Details</h2>
            <TextField
              className="mt-md"
              isInvalid={problems.details.length > 0}
              isRequired
              onChange={setName}
              value={name}
            >
              <Label requiredHint>Name</Label>
              <Input placeholder="e.g. Ads & social" />
            </TextField>
            <p className="mt-md text-sm text-text-tertiary">
              Changes apply to conversations already in progress.
            </p>
            {problems.details.length > 0 && (
              <ul className="mt-md text-sm text-text-error" role="alert">
                {problems.details.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            )}
          </section>

          <section className="p-6">
            <h2 className="text-md font-semibold text-text-primary">
              Guideline
            </h2>
            <TextField
              className="mt-md"
              onChange={setInstructions}
              value={instructions}
            >
              <Label>Guideline for the agent</Label>
              <TextArea rows={8} />
              <Description>
                Optional. Tells the agent what a good brief for this kind of
                design needs (5–10 lines).
              </Description>
            </TextField>
          </section>

          <GuidelineCriteriaSection
            criteria={criteria}
            onChange={setCriteria}
          />

          <GuidelineChecksSection
            checkKeys={checkKeys}
            checks={checks}
            criteria={criteria}
            onChange={setCheckKeys}
          />

          {guideline && (
            <GuidelineUsedBy
              assignments={assignments}
              catalog={catalog}
              guidelineId={guideline._id}
            />
          )}

          <section className="p-6">
            <h2 className="mb-md text-md font-semibold text-text-primary">
              Default
            </h2>
            <SwitchField>
              <Switch
                isDisabled={isCurrentDefault}
                isSelected={isDefault}
                onChange={setIsDefault}
              >
                Default guideline
              </Switch>
              <Description>
                {isCurrentDefault
                  ? "This is the default. Mark another guideline as default to replace it."
                  : "Used by every design type and subcategory that doesn't assign its own guideline. Replaces the current default on save."}
              </Description>
            </SwitchField>
            {isDefault && (
              <TextField
                className="mt-md"
                onChange={setDefaultNotes}
                value={defaultNotes}
              >
                <Label>Default notes for the agent</Label>
                <TextArea rows={3} />
              </TextField>
            )}
          </section>

          {problems.save.length > 0 && (
            <ul className="p-6 text-sm text-text-error" role="alert">
              {problems.save.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}

          <div className="flex items-center justify-end gap-5 px-6 py-4 text-text-primary">
            <Button onPress={goToList} variant="plain">
              Cancel
            </Button>
            <Button
              isLoading={
                saveGuideline.isPending || assignDefaultGuideline.isPending
              }
              onPress={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </FeaturePageLayout>

      <ConfirmationModal
        confirmButtonText={deleteGuideline.isPending ? "Deleting..." : "Delete"}
        icon={<Trash2 className="text-featured-icon-light-fg-error" />}
        isConfirmLoading={deleteGuideline.isPending}
        isOpen={isDeleteModalOpen}
        onConfirm={handleDelete}
        onOpenChange={setIsDeleteModalOpen}
        text="Sub categories, design types and the default must stop using this guideline first."
        title={`Delete ${guideline?.name ?? "guideline"}?`}
      />
    </div>
  );
}
